<template>
  <div ref="threeContainer" class="three-container"></div>
</template>

<script>
  import * as THREE from 'three';
  import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
  import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

  // Custom variables
  const FOV = 60; // Field of view for the camera
  const clippingClose = 0.1; // Near clipping plane
  const clippingFar = 1000; // Far clipping plane

  // Camera movement
  const dragSpeed = 0.1; // Damping factor for smooth dragging
  const x = 0; // X alignment of the camera
  const y = 0; // Y alignment of the camera
  const z = 4; // Distance of the camera from the model
  const rotSpeed = 0.005; // Rotation speed of the model

  // Light settings
  const lightColor = 0xffffff; // Color of the ambient light
  const lightBrightness = 3; // Brightness of the ambient light

  // Styling
  const bgColor = 0x000000; // Background color

  export default {
    name: 'Restroom',
    props: {
      modelPath: {
        type: String,
        required: true, // Path to the .glb model
      },
    },
    data() {
      return {
        userInteracted: false, // Tracks whether the user has interacted with the model
      };
    },
    watch: {
      modelPath: {
        immediate: true,
        handler(newPath) {
          if (newPath) {
            this.loadModel(newPath);
          }
        },
      },
    },
    mounted() {
      // Bind the animate method to the component instance
      this.animate = this.animate.bind(this);

      // Set up the scene, camera, and renderer
      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(
        FOV,
        this.$refs.threeContainer.offsetWidth /
          this.$refs.threeContainer.offsetHeight,
        clippingClose,
        clippingFar
      );
      this.renderer = new THREE.WebGLRenderer({ antialias: true });
      this.renderer.setSize(
        this.$refs.threeContainer.offsetWidth,
        this.$refs.threeContainer.offsetHeight
      );
      this.renderer.setClearColor(bgColor); // Use the constant for background color
      this.$refs.threeContainer.appendChild(this.renderer.domElement);

      // Camera controls
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true; // Enable smooth dragging
      this.controls.dampingFactor = dragSpeed; // Set damping factor
      this.camera.position.set(x, y, z); // Set camera position
      this.controls.update();

      // Detect user interaction
      this.controls.addEventListener('start', () => {
        this.userInteracted = true; // Set user interaction flag
      });

      // Lights
      const ambientLight = new THREE.AmbientLight(lightColor, lightBrightness); // Add ambient light
      this.scene.add(ambientLight);

      // Handle window resize
      window.addEventListener('resize', this.resize);
      this.resize();

      // Start animation loop
      this.animate();
    },
    methods: {
      loadModel(modelPath) {
        this.$emit('model-loading');
        // Remove any existing model from the scene
        if (this.currentModel) {
          this.scene.remove(this.currentModel);
        }

        // Load the new model
        const loader = new GLTFLoader();
        loader.load(
          modelPath,
          (gltf) => {
            this.currentModel = gltf.scene;
            this.currentModel.position.set(0, 0, 0); // Default position
            this.scene.add(this.currentModel);
            this.userInteracted = false;
            this.camera.position.set(x, y, z);
            this.controls.target.set(0, 0, 0);
            this.controls.update();
            this.$emit('model-loaded');
          },
          undefined,
          (error) => {
            console.error('An error occurred while loading the model:', error);
            this.$emit('model-loaded');
          }
        );
      },
      resize() {
        this.renderer.setSize(
          this.$refs.threeContainer.offsetWidth,
          this.$refs.threeContainer.offsetHeight
        );
        this.camera.aspect =
          this.$refs.threeContainer.offsetWidth /
          this.$refs.threeContainer.offsetHeight;
        this.camera.updateProjectionMatrix();
      },
      animate() {
        requestAnimationFrame(this.animate);

        // Rotate the model only if the user hasn't interacted
        if (!this.userInteracted && this.currentModel) {
          this.currentModel.rotation.y += rotSpeed;
        }

        this.controls.update();
        this.renderer.render(this.scene, this.camera);
      },
    },
    beforeDestroy() {
      // Clean up event listeners and resources
      window.removeEventListener('resize', this.resize);
      this.renderer.dispose();
      this.scene = null;
      this.camera = null;
      this.controls = null;
    },
  };
</script>

<style scoped>
  .three-container {
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
</style>
